import DropdownMenu from '../DropdownMenu';
import UserIcon from '../UserIcon';

import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import { shouldDisplayDropdownLabelHelp, shouldDisplayResources } from '../../utils';

import NotificationCenter from '../NotificationCenter';

// constellation
import { Divider, Flex, IconExternal, MenuItem, Text, TextButton } from '@zillow/constellation';

// style
import { StyledAnchor, StyledAnchorWithIcon } from '../DropdownMenu/style';
import { StyledMenuItemAnchor } from '../../style';
import { StyledSectionRight } from './style';

import type { Displays } from '../../interfaces/Displays';
import type { User } from '../../interfaces/user';

interface Props {
  brand: string;
  csrf_token: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  paths: any;
  pathname: string;
  proxyPath: string;
  userModel: User | null;
  displays: Displays;
  onLoginClick?(): Promise<void> | void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  webSocketInstance: any;
  helpPage: string | void;
  zgApi: string;
}

const HeaderNavigationSectionRight = ({
  brand,
  csrf_token,
  displays,
  paths,
  pathname,
  proxyPath,
  userModel,
  onLoginClick,
  webSocketInstance,
  helpPage,
  zgApi,
}: Props) => {
  const {
    billingPage,
    billingDue,
    contactSupportPage,
    resourcesPage,
    zrmSignInPage: signInPageFunc,
    mfhResourcesPage,
    postLogout,
    securitySettingsPage,
    userManagementPage,
    adManagementPage,
    setupTasks,
  } = paths;

  const { email, firstName, lastName, loggedIn, userName, profileImageUrl } = userModel ?? {};

  // functions that return paths
  const signInPage = signInPageFunc(pathname);

  const {
    displayAccessMainContactSupport,
    displayBilling,
    displayMainBilling,
    displayMFHResources,
    displayMainAccountSettings,
    displayMainHelp,
    displayMainNotificationCenter,
    displayMainResources,
    displayMainSecuritySettings,
    displayUserManagement,
    displayAdManagement,
  } = displays;

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      } else {
        // eslint-disable-next-line
        console.error('ERROR@handleLinkClick: trigger source does not exist.', csLink);
      }
    }
  };

  return (
    <StyledSectionRight>
      <Flex
        className="SectionRight-linkContainer"
        alignItems="center"
        display="flex"
        flexBasis="100%"
        flexFlow="row nowrap"
        justifyContent="flex-end"
        as="ul"
      >
        <li className="SectionRight-link" onClick={() => handleLinkClick(HeaderTitles.backToBrand)}>
          <StyledMenuItemAnchor href={paths.homePage}>{HeaderTitles.backToBrand}</StyledMenuItemAnchor>
        </li>

        {Boolean(shouldDisplayDropdownLabelHelp) && (
          <li className="SectionRight-link">
            <DropdownMenu
              title={
                <TextButton buttonType="secondary" fontType="body">
                  Help
                </TextButton>
              }
            >
              <div className="DropdownMenu-Content">
                {shouldDisplayResources(displayMFHResources, displayMainResources) && (
                  <div>
                    {displayMFHResources ? (
                      <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.resources)}>
                        <StyledAnchorWithIcon
                          title={HeaderTitles.mfhResources}
                          className="Link"
                          target="_blank"
                          href={mfhResourcesPage}
                        >
                          <Text fontType="bodySmallHeading">{HeaderTitles.mfhResources}</Text>
                          <div data-testid="IconContainer">
                            <IconExternal fontColor="brand" data-testid="IconExternal" />
                          </div>
                        </StyledAnchorWithIcon>
                      </MenuItem>
                    ) : (
                      <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.resources)}>
                        <StyledAnchorWithIcon
                          title={HeaderTitles.resources}
                          className="Link"
                          href={resourcesPage}
                          target="_blank"
                        >
                          <Text fontType="bodySmallHeading">{HeaderTitles.resources}</Text>
                          <div data-testid="IconContainer">
                            <IconExternal fontColor="brand" data-testid="IconExternal" />
                          </div>
                        </StyledAnchorWithIcon>
                      </MenuItem>
                    )}
                  </div>
                )}

                {displayMainHelp && (
                  <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.help)}>
                    <StyledAnchorWithIcon
                      title={HeaderTitles.help}
                      className="Link"
                      // @ts-ignore
                      href={helpPage}
                      target="_blank"
                    >
                      <Text fontType="bodySmallHeading">{HeaderTitles.help}</Text>
                      <div data-testid="IconContainer">
                        <IconExternal fontColor="brand" data-testid="IconExternal" />
                      </div>
                    </StyledAnchorWithIcon>
                  </MenuItem>
                )}

                {displayAccessMainContactSupport && (
                  <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.contactSupport)}>
                    <StyledAnchorWithIcon
                      title={HeaderTitles.contactSupport}
                      className="Link"
                      href={contactSupportPage}
                      target="_blank"
                    >
                      <Text fontType="bodySmallHeading">{HeaderTitles.contactSupport}</Text>
                      <div data-testid="IconContainer">
                        <IconExternal fontColor="brand" data-testid="IconExternal" />
                      </div>
                    </StyledAnchorWithIcon>
                  </MenuItem>
                )}
              </div>
            </DropdownMenu>
          </li>
        )}

        {loggedIn && displayMainNotificationCenter && (
          <li className="SectionRight-notificationCenter">
            <NotificationCenter
              brand={brand}
              proxyPath={proxyPath}
              csrf_token={csrf_token}
              webSocketInstance={webSocketInstance}
              zgApi={zgApi}
            />
          </li>
        )}

        {loggedIn && (
          <li className="SectionRight-link">
            <DropdownMenu
              title={
                <TextButton aria-label="My account" buttonType="secondary" fontType="body">
                  <UserIcon
                    profileImageUrl={profileImageUrl}
                    firstName={firstName}
                    lastName={lastName}
                    userName={userName}
                    email={email}
                  />
                </TextButton>
              }
            >
              {displayMainAccountSettings && (
                <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.accountSettings)}>
                  <StyledAnchorWithIcon
                    title={HeaderTitles.accountSettings}
                    className="Link"
                    href={paths.userSettingsPage}
                  >
                    <div>
                      <Text as="div" fontType="bodySmallHeading">
                        {HeaderTitles.accountSettings}
                      </Text>
                      <Text as="div" fontType="finePrint">
                        Managed on Zillow.com
                      </Text>
                    </div>

                    <div data-testid="IconContainer">
                      <IconExternal fontColor="brand" data-testid="IconExternal" />
                    </div>
                  </StyledAnchorWithIcon>
                </MenuItem>
              )}

              {displayMainSecuritySettings && (
                <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.securitySettings)}>
                  <StyledAnchorWithIcon
                    title={HeaderTitles.securitySettings}
                    className="Link"
                    href={securitySettingsPage}
                  >
                    <Text fontType="bodySmallHeading">{HeaderTitles.securitySettings}</Text>

                    <div data-testid="IconContainer">
                      <IconExternal fontColor="brand" data-testid="IconExternal" />
                    </div>
                  </StyledAnchorWithIcon>
                </MenuItem>
              )}

              {displayBilling && !displayMainBilling && (
                <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.billing)}>
                  <StyledAnchor title={HeaderTitles.billing} className="Link" href={`${billingPage}/${billingDue}`}>
                    <Text fontType="bodySmallHeading">{HeaderTitles.billing}</Text>
                  </StyledAnchor>
                </MenuItem>
              )}

              {displayAdManagement && (
                <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.adManagement)}>
                  <StyledAnchor
                    title={HeaderTitles.adManagement}
                    className="Link"
                    href={`${adManagementPage}/${setupTasks}`}
                  >
                    <Text fontType="bodySmallHeading">{HeaderTitles.adManagement}</Text>
                  </StyledAnchor>
                </MenuItem>
              )}

              {displayUserManagement && (
                <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.userManagement)}>
                  <StyledAnchor title={HeaderTitles.userManagement} className="Link" href={userManagementPage}>
                    <Text fontType="bodySmallHeading">{HeaderTitles.userManagement}</Text>
                  </StyledAnchor>
                </MenuItem>
              )}

              <Divider />

              <MenuItem className="MenuItem" onClick={() => handleLinkClick(HeaderTitles.signOut)}>
                <StyledAnchor title={HeaderTitles.signOut} className="Link" href={postLogout}>
                  <Text fontType="bodySmallHeading">Sign out</Text>
                </StyledAnchor>
              </MenuItem>
            </DropdownMenu>
          </li>
        )}

        {!loggedIn && (
          <li className="SectionRight-link">
            <TextButton
              title="Sign in"
              className="Link"
              fontType="body"
              onClick={() => onLoginClick && onLoginClick()}
              as="a"
              href={signInPage}
            >
              Sign in
            </TextButton>
          </li>
        )}
      </Flex>
    </StyledSectionRight>
  );
};

export default HeaderNavigationSectionRight;
